import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { deleteAiQuizQuestion, updateAiQuizQuestion } from "../../../api";
import { GhostButton, PrimaryButton } from "../items/shared";

/**
 * QuestionEditor — staff modal to hand-correct one AI quiz question
 * (docs/AI_TOPIC_QUIZ.md): typos in prompts / choices / sentences, a wrong
 * correct answer, a misleading explanation. Saves go through the server's
 * generated-question validator; the question keeps its id so attempts in
 * progress and seen-tracking still resolve.
 *
 * Choices are edited with a radio for the correct one; on save they are sent
 * correct-first with correct_index 0 (the app's authoring convention).
 */
const AR_TYPES_WITH_CHOICES = { mc: true, audio_mc: true, cloze_choice: true };

const inputCls = "w-full rounded-lg px-3 py-2 text-sm";
const inputStyle = { background: "var(--ink)", border: "1px solid var(--panel-line)", color: "var(--parchment)" };
const arInputCls = "w-full rounded-lg px-3 py-2 font-arabic text-xl leading-[1.8]";

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide block mb-1" style={{ color: "var(--parchment-dim)" }}>{label}</span>
      {children}
      {hint && <span className="text-[11px] block mt-1" style={{ color: "var(--parchment-dim)" }}>{hint}</span>}
    </label>
  );
}

function TextInput({ value, onChange, arabic = false, multiline = false, placeholder }) {
  const props = {
    value: value ?? "",
    onChange: (e) => onChange(e.target.value),
    dir: arabic ? "rtl" : undefined,
    lang: arabic ? "ar" : undefined,
    className: arabic ? arInputCls : inputCls,
    style: inputStyle,
    placeholder,
  };
  return multiline ? <textarea rows={arabic ? 2 : 3} {...props} /> : <input type="text" {...props} />;
}

/** Choice list with a radio for the correct one. */
function ChoicesEditor({ choices, correctIndex, onChange, arabic }) {
  const set = (i, v) => onChange(choices.map((c, j) => (j === i ? v : c)), correctIndex);
  const remove = (i) => {
    const next = choices.filter((_, j) => j !== i);
    onChange(next, Math.max(0, correctIndex === i ? 0 : correctIndex > i ? correctIndex - 1 : correctIndex));
  };
  return (
    <div className="flex flex-col gap-2">
      {choices.map((c, i) => (
        <div key={i} className="flex items-center gap-2">
          <input type="radio" name="correct" checked={correctIndex === i} onChange={() => onChange(choices, i)} title="Correct answer" />
          <TextInput value={c} onChange={(v) => set(i, v)} arabic={arabic} />
          <button type="button" onClick={() => remove(i)} disabled={choices.length <= 3} className="text-xs px-2 disabled:opacity-30" style={{ color: "var(--parchment-dim)" }} title="Remove">✕</button>
        </div>
      ))}
      {choices.length < 5 && (
        <button type="button" onClick={() => onChange([...choices, ""], correctIndex)} className="text-xs underline self-start" style={{ color: "var(--parchment-dim)" }}>
          + add a choice
        </button>
      )}
      <span className="text-[11px]" style={{ color: "var(--parchment-dim)" }}>The radio marks the correct answer. 3–5 choices.</span>
    </div>
  );
}

export default function QuestionEditor({ question, onSaved, onDeleted, onClose }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(question)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const d = draft.data || {};
  const setTop = (k, v) => setDraft((q) => ({ ...q, [k]: v }));
  const setData = (k, v) => setDraft((q) => ({ ...q, data: { ...(q.data || {}), [k]: v } }));

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = { ...(draft.data || {}) };
      if (AR_TYPES_WITH_CHOICES[draft.type] && Array.isArray(data.choices)) {
        const ci = data.correct_index ?? 0;
        const cleaned = data.choices.map((c) => String(c || "").trim());
        data.choices = [cleaned[ci], ...cleaned.filter((_, i) => i !== ci)];
        data.correct_index = 0;
      }
      const saved = await updateAiQuizQuestion(question.bank_id, question.id, {
        prompt_en: draft.prompt_en,
        explanation: draft.explanation,
        difficulty: draft.difficulty,
        audio_text: draft.audio_text,
        data,
      });
      onSaved(saved);
    } catch (e) {
      setError(e.message || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const toggleHidden = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await updateAiQuizQuestion(question.bank_id, question.id, { hidden: !question.hidden });
      onSaved(saved);
    } catch (e) {
      setError(e.message || "Could not update.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteAiQuizQuestion(question.bank_id, question.id);
      onDeleted(question);
    } catch (e) {
      setError(e.message || "Could not delete.");
      setBusy(false);
    }
  };

  const arabicChoices = draft.type === "cloze_choice" || (draft.type === "mc" && ["en_ar", "best_reply"].includes(d.variant))
    || (draft.type === "mc" && Array.isArray(d.choices) && d.choices.some((c) => /[؀-ۿ]/.test(c)));

  const body = (
    <div className="flex flex-col gap-4">
      <Field label="Prompt (English)"><TextInput value={draft.prompt_en} onChange={(v) => setTop("prompt_en", v)} /></Field>

      {draft.type === "mc" && (
        <>
          {d.variant !== "en_ar" && (
            <Field label={d.variant === "ar_en" ? "Word (Arabic)" : "Dialogue line (Arabic)"}>
              <TextInput value={d.prompt_ar} onChange={(v) => setData("prompt_ar", v)} arabic />
            </Field>
          )}
          <Field label="Choices">
            <ChoicesEditor choices={d.choices || []} correctIndex={d.correct_index ?? 0} arabic={arabicChoices}
                           onChange={(choices, ci) => setDraft((q) => ({ ...q, data: { ...q.data, choices, correct_index: ci } }))} />
          </Field>
        </>
      )}

      {draft.type === "audio_mc" && (
        <>
          <Field label="Sentence played (Arabic)"><TextInput value={d.audio_text} onChange={(v) => setData("audio_text", v)} arabic /></Field>
          <Field label="Choices (English)">
            <ChoicesEditor choices={d.choices || []} correctIndex={d.correct_index ?? 0} arabic={false}
                           onChange={(choices, ci) => setDraft((q) => ({ ...q, data: { ...q.data, choices, correct_index: ci } }))} />
          </Field>
        </>
      )}

      {draft.type === "cloze_choice" && (
        <>
          <Field label="Sentence with the gap (Arabic)" hint="Use ___ for the gap — exactly one.">
            <TextInput value={d.sentence_ar} onChange={(v) => setData("sentence_ar", v)} arabic />
          </Field>
          <Field label="Full sentence (Arabic)"><TextInput value={d.full_ar} onChange={(v) => setData("full_ar", v)} arabic /></Field>
          <Field label="Translation (English)"><TextInput value={d.sentence_en} onChange={(v) => setData("sentence_en", v)} /></Field>
          <Field label="Choices (Arabic)">
            <ChoicesEditor choices={d.choices || []} correctIndex={d.correct_index ?? 0} arabic
                           onChange={(choices, ci) => setDraft((q) => ({ ...q, data: { ...q.data, choices, correct_index: ci } }))} />
          </Field>
        </>
      )}

      {draft.type === "true_false" && (
        <>
          <Field label="Statement (English)"><TextInput value={d.statement_en} onChange={(v) => setData("statement_en", v)} /></Field>
          <Field label="Answer">
            <div className="flex gap-4 text-sm" style={{ color: "var(--parchment)" }}>
              <label className="flex items-center gap-1.5"><input type="radio" checked={d.is_true === true} onChange={() => setData("is_true", true)} /> True</label>
              <label className="flex items-center gap-1.5"><input type="radio" checked={d.is_true === false} onChange={() => setData("is_true", false)} /> False</label>
            </div>
          </Field>
          <Field label="Deciding line (Arabic, optional)"><TextInput value={d.evidence_ar} onChange={(v) => setData("evidence_ar", v)} arabic /></Field>
        </>
      )}

      {draft.type === "match_pairs" && (
        <Field label="Pairs">
          <div className="flex flex-col gap-2">
            {(d.pairs || []).map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                <TextInput value={p.ar} onChange={(v) => setData("pairs", d.pairs.map((x, j) => (j === i ? { ...x, ar: v } : x)))} arabic />
                <TextInput value={p.en} onChange={(v) => setData("pairs", d.pairs.map((x, j) => (j === i ? { ...x, en: v } : x)))} />
                <button type="button" onClick={() => setData("pairs", d.pairs.filter((_, j) => j !== i))} disabled={(d.pairs || []).length <= 3} className="text-xs px-2 disabled:opacity-30" style={{ color: "var(--parchment-dim)" }}>✕</button>
              </div>
            ))}
            {(d.pairs || []).length < 6 && (
              <button type="button" onClick={() => setData("pairs", [...(d.pairs || []), { ar: "", en: "" }])} className="text-xs underline self-start" style={{ color: "var(--parchment-dim)" }}>+ add a pair</button>
            )}
          </div>
        </Field>
      )}

      {draft.type === "bucket_sort" && (
        <>
          <Field label="Groups">
            <div className="flex flex-col gap-2">
              {(d.buckets || []).map((b, i) => (
                <div key={b.key} className="grid grid-cols-[1fr_1fr] gap-2">
                  <TextInput value={b.label_en} placeholder="English label" onChange={(v) => setData("buckets", d.buckets.map((x, j) => (j === i ? { ...x, label_en: v } : x)))} />
                  <TextInput value={b.label_ar} placeholder="Arabic label" arabic onChange={(v) => setData("buckets", d.buckets.map((x, j) => (j === i ? { ...x, label_ar: v } : x)))} />
                </div>
              ))}
            </div>
          </Field>
          <Field label="Words and their group">
            <div className="flex flex-col gap-2">
              {(d.items || []).map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
                  <TextInput value={it.ar} arabic onChange={(v) => setData("items", d.items.map((x, j) => (j === i ? { ...x, ar: v } : x)))} />
                  <TextInput value={it.en} onChange={(v) => setData("items", d.items.map((x, j) => (j === i ? { ...x, en: v } : x)))} />
                  <select value={it.bucket} onChange={(e) => setData("items", d.items.map((x, j) => (j === i ? { ...x, bucket: e.target.value } : x)))} className="rounded-lg px-2 py-2 text-sm" style={inputStyle}>
                    {(d.buckets || []).map((b) => <option key={b.key} value={b.key}>{b.label_en}</option>)}
                  </select>
                  <button type="button" onClick={() => setData("items", d.items.filter((_, j) => j !== i))} disabled={(d.items || []).length <= 4} className="text-xs px-2 disabled:opacity-30" style={{ color: "var(--parchment-dim)" }}>✕</button>
                </div>
              ))}
              {(d.items || []).length < 10 && (
                <button type="button" onClick={() => setData("items", [...(d.items || []), { ar: "", en: "", bucket: d.buckets?.[0]?.key }])} className="text-xs underline self-start" style={{ color: "var(--parchment-dim)" }}>+ add a word</button>
              )}
            </div>
          </Field>
        </>
      )}

      {draft.type === "order_sentence" && (
        <>
          <Field label="Line (Arabic)" hint="Words are split on spaces to make the chips."><TextInput value={d.sentence_ar} onChange={(v) => setData("sentence_ar", v)} arabic /></Field>
          <Field label="Translation (English)"><TextInput value={d.sentence_en} onChange={(v) => setData("sentence_en", v)} /></Field>
        </>
      )}

      {draft.type === "verb_form" && (
        <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>
          The forms come from the verb's own data — fix them on the word itself. Only the wording here is editable.
        </p>
      )}

      <Field label="Explanation (shown after answering)"><TextInput value={draft.explanation} onChange={(v) => setTop("explanation", v)} multiline /></Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Difficulty">
          <select value={draft.difficulty || 1} onChange={(e) => setTop("difficulty", Number(e.target.value))} className={inputCls} style={inputStyle}>
            <option value={1}>1 — recognition</option><option value={2}>2 — recall in context</option><option value={3}>3 — production</option>
          </select>
        </Field>
        {draft.type !== "audio_mc" && (
          <Field label="Audio text (Arabic, optional)"><TextInput value={draft.audio_text} onChange={(v) => setTop("audio_text", v)} arabic /></Field>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <div className="dark-scope fixed inset-0 z-[90] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}
         onClick={(e) => e.target === e.currentTarget && onClose()} role="dialog" aria-modal="true">
      <div className="surface-glass w-full max-w-2xl max-h-[92vh] overflow-y-auto scroll-elegant rounded-2xl p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-xs uppercase tracking-wide m-0" style={{ color: "var(--parchment-dim)" }}>Edit question · {question.type}{question.data?.variant ? ` / ${question.data.variant}` : ""}</p>
            <p className="text-[11px] m-0 mt-0.5" style={{ color: "var(--parchment-dim)" }}>
              {question.edited ? "Hand-edited" : "As generated"}{question.hidden ? " · hidden from learners" : ""}{question.reports?.length ? ` · ${question.reports.length} report(s)` : ""}
            </p>
          </div>
          <GhostButton onClick={onClose}>Close</GhostButton>
        </div>

        {question.reports?.length > 0 && (
          <div className="mb-4 px-3 py-2 rounded-xl text-xs" style={{ background: "rgba(220,90,90,0.10)", border: "1px solid #dc5a5a", color: "var(--parchment)" }}>
            {question.reports.map((r, i) => <div key={i}>• {r.reason.replace("_", " ")}{r.note ? ` — ${r.note}` : ""} <span style={{ color: "var(--parchment-dim)" }}>({r.user})</span></div>)}
          </div>
        )}

        {body}

        {error && <p className="text-xs mt-3 mb-0" style={{ color: "#e58c8c" }}>{error}</p>}

        <div className="flex flex-wrap items-center justify-between gap-2 mt-5">
          <div className="flex items-center gap-2">
            <GhostButton onClick={toggleHidden} disabled={busy}>{question.hidden ? "Unhide" : "Hide from learners"}</GhostButton>
            {confirmDelete ? (
              <>
                <span className="text-xs" style={{ color: "#e58c8c" }}>Delete for good?</span>
                <GhostButton onClick={remove} disabled={busy}>Yes, delete</GhostButton>
                <GhostButton onClick={() => setConfirmDelete(false)}>No</GhostButton>
              </>
            ) : (
              <GhostButton onClick={() => setConfirmDelete(true)} disabled={busy}>Delete</GhostButton>
            )}
          </div>
          <PrimaryButton onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</PrimaryButton>
        </div>
      </div>
    </div>,
    document.body
  );
}
